import { slugify } from "./slug";

/**
 * Centralized route definitions.
 * - `paths`: Route patterns for <Route path="..."> definitions
 * - `to`: Functions to generate URLs for navigation/links
 */
export const routes = {
  paths: {
    home: "/",
    auth: {
      signIn: "/auth/signin",
      verify: "/auth/verify",
    },
    invite: {
      accept: "/invite/:token",
    },
    maps: {
      root: "/maps",
      new: "/maps/new",
      view: "/maps/:id",
      landing: "/maps/:id/landing",
      // Public viewer - identifier can be "{name}-{shareId}" or legacy slug
      publicView: "/m/:identifier",
    },
    landingPages: {
      root: "/",
      bySlug: "/p/:slug",
      layoutShowcase: "/layouts",
    },
  },

  to: {
    home: () => "/",
    auth: {
      signIn: () => "/auth/signin",
      verify: () => "/auth/verify",
    },
    invite: {
      accept: (token: string) => `/invite/${token}`,
    },
    maps: {
      root: () => "/maps",
      new: () => "/maps/new",
      view: (id: string) => `/maps/${id}`,
      landing: (id: string) => `/maps/${id}/landing`,
      // Build Notion-style URL: /m/{slugified-name}-{shareId}
      publicView: (name: string, shareId: string) => {
        const slugifiedName = slugify(name);
        return `/m/${slugifiedName}-${shareId}`;
      },
      publicViewUrl: (name: string, shareId: string) => {
        const slugifiedName = slugify(name);
        return `${window.location.origin}/m/${slugifiedName}-${shareId}`;
      },
    },
    landingPages: {
      bySlug: (slug: string) => {
        const { protocol, host } = window.location;
        const rootDomain = getRootDomain(host);

        // Subdomain routing only for localhost and production noma.to (wildcard DNS/SSL)
        // Everything else (staging, v2, Cloud Run previews) uses path-based routing
        const isLocalhost = rootDomain.startsWith("localhost");
        const isProduction =
          host === "noma.to" || (host.endsWith(".noma.to") && !host.startsWith("staging.") && !host.startsWith("v2."));

        if (isLocalhost || isProduction) {
          return `${protocol}//${slug}.${rootDomain}`;
        }
        return `${protocol}//${host}/p/${slug}`;
      },
      layoutShowcase: () => "/layouts",
    },
  },
} as const;

/** Extract root domain, stripping any subdomain prefix */
function getRootDomain(host: string): string {
  if (host.endsWith(".noma.to") || host === "noma.to") return "noma.to";
  const localhostMatch = host.match(/^(?:[^.]+\.)?(localhost(?::\d+)?)$/);
  if (localhostMatch) return localhostMatch[1];
  return host;
}
